import React, {useMemo} from "react";
import {Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import {getProtoTimestampAsDateString} from "../helper";
import {
  StyledBackButton,
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalTitle
} from "../styles";

interface ConfirmModalProps {
    isOpen: boolean;
    handleOnHideModal: () => void;
    handleOnConfirmModal: () => void;
    content: ConfirmModalContentProps;
}

export interface ConfirmModalContentProps {
    currentLevel: string;
    newLevel: string;
    expirationDate: string;
    transactionNumber: number;
    amount: number;
    currency: string;
}

const messages = defineMessages({
    modalConfirmTransactionNumberQuantity: {
        id: "loyaltyCustomerHistorical.modalConfirm.transactionNumber.quantity",
        defaultMessage: "As of today, {transactionNumber} booking will be added"
    },
    modalConfirmSpentAmount: {
        id: "loyaltyCustomerHistorical.modalConfirm.spentAmount.amount",
        defaultMessage: "{currency} {amount} reservations amount will be added as of today's date"
    },
    modalConfirmNewLevel: {
        id: "loyaltyCustomerHistorical.modalConfirm.level",
        defaultMessage: "The new {level} level will be active until {expirationDate}",
    },
    lineOne: {
        id: "loyaltyCustomerHistorical.modalConfirm.changesReflected",
        defaultMessage: "The changes will be reflected after 24 hours.",
    },
    lineTwo: {
        id: "loyaltyCustomerHistorical.modalConfirm.question",
        defaultMessage: "Do you want to make the change?",
    },
    no: {
        id: "default.no",
        defaultMessage: "No",
    },
    ok: {
        id: "default.ok",
        defaultMessage: "Yes",
    },
    tooltip: {
        id: "loyaltyCustomerHistorical.modalConfirm.tooltip",
        defaultMessage: "With the modification, the minimum number of reservations or amount to reach the desired level is calculated and added with the current date, taking into account the guest's reservation history. The expiration date will be calculated taking into account the history of reservations",
    }
});

export function ConfirmModal(props: ConfirmModalProps) {
    const intl = useIntl();

    const hasSameLevel = useMemo(() => props.content.currentLevel === props.content.newLevel,[props]);

    const tooltipConfig = useMemo(() => ({
        delay: { show: 250, hide: 0 }
    }), []);

    const infoTooltip = useMemo(() => (
        <Tooltip id="tooltip">
            <FormattedMessage {...messages.tooltip} />
        </Tooltip>
    ), []);

    const contentTitle = useMemo(() => {
        if (props.content.transactionNumber !== undefined && props.content.transactionNumber > 0) {
            return intl.formatMessage(messages.modalConfirmTransactionNumberQuantity, {
                transactionNumber: props.content.transactionNumber
            });
        }
        return intl.formatMessage(messages.modalConfirmSpentAmount, {
            amount: props.content.amount,
            currency: props.content.currency,
        });
    }, [intl, props]);

    const contentNewLevel = useMemo(() => {
        return intl.formatMessage(messages.modalConfirmNewLevel, {
            level: props.content.newLevel,
            expirationDate: getProtoTimestampAsDateString(props.content.expirationDate) || "N/A"
        })
    }, [intl, props]);

    return (
        <StyledModal size={"lg"} centered show={props.isOpen}>
            <StyledModalHeader>
                <StyledModalTitle>
                    <div>
                        <p>{contentTitle}</p>
                        {!hasSameLevel && (
                            <>
                                <span className="mr-2">
                                    {contentNewLevel}
                                </span>
                                <OverlayTrigger {...tooltipConfig} overlay={infoTooltip}>
                                    <FontAwesomeIcon icon={faInfoCircle} color="#B3B3B3" size="xs" />
                                </OverlayTrigger>
                            </>
                        )}
                    </div>
                </StyledModalTitle>
            </StyledModalHeader>
            <StyledModalBody>
                <p>{intl.formatMessage(messages.lineOne)}</p>
                <p>{intl.formatMessage(messages.lineTwo)}</p>
            </StyledModalBody>
            <StyledModalFooter>
                <Button variant="primary" onClick={props.handleOnConfirmModal}>
                    {intl.formatMessage(messages.ok)}
                </Button>
                <StyledBackButton onClick={props.handleOnHideModal}>
                    {intl.formatMessage(messages.no)}
                </StyledBackButton>
            </StyledModalFooter>
        </StyledModal>
    )
}